import { useCallback } from 'react';
import { getIn, useFormikContext } from 'formik';
import type { ChangeEvent, ChangeEventHandler } from 'react';

import LoopIcon from '@mui/icons-material/Loop';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Tooltip from '@mui/material/Tooltip';
import MuiTextField from '@mui/material/TextField';

import {
  ResolverReturnTypeEnum,
  ResolvableModeEnum,
} from '@e-socna/data-layer';
import { BooleanFilter, invariant, isDefined } from '@e-socna/utils';
import { EMPTY_OBJECT, EMPTY_STRING } from '@e-socna/empty';
import type { ResolverId } from '@e-socna/data-layer';

import { useContentGenerator } from '../../components/DocumentEditor/hooks/useContentGenerator.hook';
import { useReferencesContext } from '../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import { wrapExpression } from '../../utils/handlebars-expression.util';

import { DEFAULT_MODE } from './constants/default-mode.constant';
import { parseStaticNumberInput } from './utils/parse-static-number-input.util';
import type { TextFieldProps } from './types/props.type';

export const TextField = ({
  id,
  label,
  name,
  onOpenResolverSelectionDialog: handleOpenResolverSelectionDialog,
  required,
  resolvableFieldSettings,
  slotProps,
  type,
  value,
}: TextFieldProps) => {
  const currentMode = value?.mode ?? DEFAULT_MODE;
  const isInDynamicMode = currentMode === ResolvableModeEnum.Dynamic;
  invariant(
    isInDynamicMode ||
      [ResolverReturnTypeEnum.Float, ResolverReturnTypeEnum.Integer].includes(
        type,
      ),
    'Textfield component only supports float & integer values',
  );

  const { errors, handleBlur, setFieldTouched, setFieldValue, touched } =
    useFormikContext();

  const { resolversIdMap } = useReferencesContext();
  const resolver = value?.resolverId
    ? resolversIdMap[value.resolverId as ResolverId]
    : undefined;

  const { enableDirectGeneration, primaryMode } = resolvableFieldSettings;

  const { data, isSuccess } = useContentGenerator({
    template: isInDynamicMode
      ? wrapExpression(resolver?.command ?? EMPTY_STRING)
      : undefined,
    enabled: !!(isInDynamicMode && enableDirectGeneration && resolver),
  });

  const adornmentLabel = 'Changer de Résolveur';
  const loadingLabel = 'Chargement ...';

  invariant(
    !isInDynamicMode || !!resolver,
    'TextField: resolver must be defined when in dynamic mode',
  );

  const handleChangeStaticValue = useCallback<
    ChangeEventHandler<HTMLInputElement>
  >(
    (event: Readonly<ChangeEvent<HTMLInputElement>>) => {
      void setFieldValue(name, {
        ...value,
        value: parseStaticNumberInput(event.target.value),
      });

      void setFieldTouched(name, true, false);
    },
    [name, setFieldTouched, setFieldValue, value],
  );

  const getValue = () =>
    enableDirectGeneration
      ? isSuccess
        ? data?.generated
        : loadingLabel
      : undefined;

  const getResolver = () =>
    resolver ? wrapExpression(resolver.code) : undefined;

  const content = isInDynamicMode
    ? primaryMode === ResolvableModeEnum.Static
      ? { primary: getValue(), secondary: getResolver() }
      : { primary: getResolver(), secondary: getValue() }
    : { primary: value?.value, secondary: undefined };

  const orderedContent =
    !isDefined(content.primary) && isDefined(content.secondary)
      ? { primary: content.secondary, secondary: content.primary }
      : content;

  const getFieldErrors = (name: string) => {
    const fieldErrors = getIn(errors, name) as
      Record<string, string> | string | undefined;

    if (!fieldErrors) {
      return undefined;
    }

    if (typeof fieldErrors === 'string') {
      return fieldErrors;
    }

    return Object.values(fieldErrors).filter(BooleanFilter).join(', ');
  };

  return (
    <MuiTextField
      disabled={isInDynamicMode}
      error={!!(getIn(errors, name) && getIn(touched, name))}
      helperText={getFieldErrors(name) ?? orderedContent.secondary}
      id={id}
      label={label}
      name={name}
      onBlur={handleBlur}
      onChange={!isInDynamicMode ? handleChangeStaticValue : undefined}
      required={required}
      slotProps={{
        ...slotProps,
        ...(isInDynamicMode
          ? {
              input: {
                ...(typeof slotProps?.input === 'object' &&
                isDefined(slotProps.input)
                  ? slotProps.input
                  : EMPTY_OBJECT),
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title={adornmentLabel}>
                      <IconButton
                        aria-label={adornmentLabel}
                        edge="end"
                        onClick={handleOpenResolverSelectionDialog}
                      >
                        <LoopIcon />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
              },
            }
          : EMPTY_OBJECT),
      }}
      sx={{ flexGrow: 1 }}
      type={isInDynamicMode ? 'text' : 'number'}
      value={orderedContent.primary}
      variant="outlined"
    />
  );
};
